// src/pages/POReception.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  where,
  limit,
} from "firebase/firestore";
import { db, auth } from "../services/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { receiveFromPO } from "../services/inventoryApi";

const DEFAULT_LOCATION_ID = "boutique";

export default function POReception() {
  const { poId } = useParams();
  const [po, setPo] = useState(null);       // { id, supplier, status, items: [...] }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");

  // cache: itemId -> {barcode, name}
  const [itemMeta, setItemMeta] = useState({});
  // staged réception: itemId -> qty (pour cette session)
  const [staged, setStaged] = useState({});

  // UI: search/scan
  const [term, setTerm] = useState("");
  const inputRef = useRef(null);

  // charge le PO + métadonnées articles (barcodes)
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const ref = doc(db, "purchaseOrders", poId);
        const snap = await getDoc(ref);
        if (!snap.exists()) throw new Error("PO introuvable");
        const data = { id: snap.id, ...snap.data() };
        if (!Array.isArray(data.items)) data.items = [];
        // charge barcodes depuis itemsEK pour les itemId contenus dans le PO
        const ids = Array.from(new Set(data.items.map((l) => l.itemId).filter(Boolean)));
        const meta = {};
        if (ids.length) {
          // requêtes par lot (where in) sur ids par paquets de 10 (limite Firestore)
          const chunk = (arr, size) => Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
            arr.slice(i * size, i * size + size)
          );
          const chunks = chunk(ids, 10);
          for (const c of chunks) {
            const q = query(collection(db, "itemsEK"), where("__name__", "in", c));
            const s = await getDocs(q);
            s.forEach((d) => {
              const it = d.data() || {};
              meta[d.id] = {
                barcode: it.barcode || "",
                name: it.name || it.title || "",
              };
            });
          }
        }
        if (!alive) return;
        setPo(data);
        setItemMeta(meta);
      } catch (e) {
        console.error(e);
        if (alive) setError(e.message || "Erreur de chargement");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [poId]);

  // pour filtrer dans le tableau par recherche nom/CB
  const filteredItems = useMemo(() => {
    if (!po?.items?.length) return [];
    const t = term.trim().toLowerCase();
    if (!t) return po.items;
    return po.items.filter((l) => {
      const nm = (l.name || itemMeta[l.itemId]?.name || "").toLowerCase();
      const cb = (itemMeta[l.itemId]?.barcode || "").toLowerCase();
      return nm.includes(t) || cb.includes(t);
    });
  }, [po, term, itemMeta]);

  // utils
  function remainOf(line) {
    const qOrd = Number(line.qtyOrdered || 0);
    const qRec = Number(line.qtyReceived || 0);
    return Math.max(0, qOrd - qRec);
  }
  function stagedQty(itemId) {
    return Number(staged[itemId] || 0);
  }
  function setStagedQty(itemId, qty) {
    setStaged((p) => ({ ...p, [itemId]: Math.max(0, Math.floor(Number(qty || 0))) }));
  }
  function inc(itemId, step = 1) {
    const line = po.items.find((x) => x.itemId === itemId);
    if (!line) return;
    const remain = remainOf(line);
    const cur = stagedQty(itemId);
    setStagedQty(itemId, Math.min(remain, cur + step));
  }
  function dec(itemId, step = 1) {
    const cur = stagedQty(itemId);
    setStagedQty(itemId, Math.max(0, cur - step));
  }
  function clearItem(itemId) {
    setStaged((p) => {
      const n = { ...p };
      delete n[itemId];
      return n;
    });
  }

  // scan code-barres: incrémente la ligne correspondante si présente dans le PO
  async function onScan(code) {
    const codeTrim = String(code || "").trim();
    if (!codeTrim) return;
    // essaie de retrouver l'itemId dans le cache des barcodes
    let found = Object.entries(itemMeta).find(([id, meta]) => meta.barcode === codeTrim);
    if (!found) {
      // si pas en cache, tente lookup (au cas où)
      const q = query(collection(db, "itemsEK"), where("barcode", "==", codeTrim), limit(1));
      const s = await getDocs(q);
      if (!s.empty) {
        const d = s.docs[0];
        found = [d.id, { barcode: d.data().barcode || "", name: d.data().name || d.data().title || "" }];
        // mets à jour le cache local
        setItemMeta((p) => ({ ...p, [d.id]: found[1] }));
      }
    }
    if (!found) {
      setMsg(`❌ Code inconnu dans ce PO : ${codeTrim}`);
      return;
    }
    const [itemId] = found;
    const line = po.items.find((x) => x.itemId === itemId);
    if (!line) {
      setMsg(`❌ Article non présent dans ce PO`);
      return;
    }
    const remain = remainOf(line);
    if (remain <= 0) {
      setMsg(`ℹ️ ${line.name || itemMeta[itemId]?.name || "Article"} déjà totalement reçu`);
      return;
    }
    inc(itemId, 1);
    setMsg(`✅ +1 ${line.name || itemMeta[itemId]?.name || "Article"}`);
  }

  // confirme la réception des staged (non vides)
  async function confirmReception() {
    const itemsToReceive = Object.entries(staged)
      .filter(([, q]) => Number(q) > 0)
      .map(([itemId, q]) => ({ itemId, qty: Number(q) }));
    if (!itemsToReceive.length) {
      setMsg("Aucune quantité à recevoir.");
      return;
    }
    setSaving(true);
    setError("");
    setMsg("");

    // récup user
    let user = auth.currentUser;
    if (!user) {
      await new Promise((res) => onAuthStateChanged(auth, (u) => { user = u; res(); }));
    }
    try {
      await receiveFromPO({
        poId,
        locationId: DEFAULT_LOCATION_ID,
        items: itemsToReceive,
        receivedBy: user?.uid || "anonymous",
      });
      setMsg("✅ Réception enregistrée.");
      setStaged({});
      // recharger PO à jour
      const snap = await getDoc(doc(db, "purchaseOrders", poId));
      if (snap.exists()) setPo({ id: snap.id, ...snap.data() });
    } catch (e) {
      console.error(e);
      setError(e.message || "Erreur lors de la réception");
    } finally {
      setSaving(false);
      inputRef.current?.focus();
    }
  }

  const stagedCount = useMemo(
    () => Object.values(staged).reduce((s, n) => s + Number(n || 0), 0),
    [staged]
  );

  if (loading) {
    return (
      <div className="container py-4">
        <Link to="/purchases" className="btn btn-link">&larr; Retour</Link>
        <div className="mt-3">
          <span className="spinner-border me-2" /> Chargement…
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="container py-4">
        <Link to="/purchases" className="btn btn-link">&larr; Retour</Link>
        <div className="alert alert-danger mt-3">{error}</div>
      </div>
    );
  }
  if (!po) return null;

  return (
    <div className="container">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div>
          <Link to="/purchases" className="btn btn-link p-0 me-2">&larr; Retour</Link>
          <h3 className="d-inline-block mb-0">PO #{po.id.slice(0,6)}</h3>
          <span className={`badge ms-2 ${po.status === "closed" ? "bg-success" : po.status === "partial" ? "bg-warning text-dark" : "bg-secondary"}`}>
            {po.status || "open"}
          </span>
        </div>
        <div className="text-end">
          <div className="small text-muted">Fournisseur</div>
          <div className="fw-semibold">{po.supplier || "—"}</div>
        </div>
      </div>

      {/* Scan / Recherche */}
      <div className="card p-3 mb-3">
        <div className="row g-2 align-items-center">
          <div className="col-12 col-md-6">
            <label className="form-label mb-1">Scanner code-barres</label>
            <input
              ref={inputRef}
              className="form-control"
              placeholder="Scanner ici (focus actif)…"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const v = e.currentTarget.value.trim();
                  if (v) onScan(v);
                  e.currentTarget.value = "";
                }
              }}
              autoFocus
            />
            <div className="form-text">
              Flasher un article du PO : la quantité « Réception en cours » s’incrémente.
            </div>
          </div>
          <div className="col-12 col-md-6">
            <label className="form-label mb-1">Recherche (nom / code-barres)</label>
            <input
              className="form-control"
              placeholder="Tapez une partie de nom ou un code-barres…"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
            />
          </div>
        </div>

        {(msg || error) && (
          <div className={`alert mt-3 py-2 ${error ? "alert-danger" : msg?.startsWith("✅") ? "alert-success" : "alert-info"}`}>
            {error || msg}
          </div>
        )}
      </div>

      {/* Tableau des lignes */}
      <div className="card">
        <div className="table-responsive">
          <table className="table align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>Article</th>
                <th>Code-barres</th>
                <th className="text-end">Commandé</th>
                <th className="text-end">Reçu</th>
                <th className="text-end">Reste</th>
                <th style={{ width: 240 }}>Réception en cours</th>
                <th style={{ width: 200 }}></th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((l) => {
                const meta = itemMeta[l.itemId] || {};
                const remain = remainOf(l);
                const cur = stagedQty(l.itemId);
                return (
                  <tr key={l.itemId}>
                    <td className="fw-semibold">
                      {l.name || meta.name || "Article"}
                      <div className="small text-muted">{l.itemId}</div>
                    </td>
                    <td>{meta.barcode || "—"}</td>
                    <td className="text-end">{Number(l.qtyOrdered || 0).toLocaleString()}</td>
                    <td className="text-end">{Number(l.qtyReceived || 0).toLocaleString()}</td>
                    <td className="text-end">{remain.toLocaleString()}</td>
                    <td>
                      <div className="input-group">
                        <button className="btn btn-outline-secondary" onClick={() => dec(l.itemId)} disabled={cur <= 0}>−</button>
                        <input
                          className="form-control text-center"
                          value={cur}
                          onChange={(e) => {
                            const v = Math.max(0, Math.floor(Number(e.target.value || 0)));
                            setStagedQty(l.itemId, Math.min(remain, v));
                          }}
                        />
                        <button className="btn btn-outline-secondary" onClick={() => inc(l.itemId)} disabled={cur >= remain}>+</button>
                      </div>
                    </td>
                    <td className="d-flex gap-2">
                      <button className="btn btn-outline-secondary" onClick={() => inc(l.itemId, Math.min(5, remain))} disabled={remain <= 0}>+5</button>
                      <button className="btn btn-outline-secondary" onClick={() => inc(l.itemId, Math.min(10, remain))} disabled={remain <= 0}>+10</button>
                      <button className="btn btn-outline-danger" onClick={() => clearItem(l.itemId)} disabled={cur <= 0}>Effacer</button>
                    </td>
                  </tr>
                );
              })}
              {!filteredItems.length && (
                <tr>
                  <td colSpan={7} className="text-center text-muted py-4">Aucune ligne</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer actions */}
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="text-muted">
          Lignes : {po.items.length} • Réception en cours : <strong>{stagedCount}</strong> unités
        </div>
        <div className="d-flex gap-2">
          <Link to="/purchases" className="btn btn-outline-secondary">Annuler</Link>
          <button className="btn btn-dark" onClick={confirmReception} disabled={saving || !Object.values(staged).some((n) => Number(n) > 0)}>
            {saving ? <><span className="spinner-border spinner-border-sm me-2" /> Enregistrement…</> : "Enregistrer la réception"}
          </button>
        </div>
      </div>
    </div>
  );
}
